import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { api } from '@/lib/api-client';

/**
 * Hooks da página `/chat` (Sprint 22).
 *
 * Polling:
 *   - `useConversas` (30s) é o motor — varre a lista com `ultimaMensagem` e
 *     `naoLidas` por conversa. Toda derivação (badge da sidebar, total
 *     não-lidas, toast de "nova mensagem") consome esse mesmo cache para
 *     evitar requisições duplicadas.
 *   - `useMensagens(conversaId)` faz polling de 10s da conversa aberta.
 *
 * 422 silencioso em `useConfirmarLeitura`: a chamada automática via
 * IntersectionObserver pode atingir mensagens que perderam a flag de
 * confirmação no meio do caminho — não vale toastar erro pro usuário.
 */

// ---------------------------------------------------------------------------
// Tipos do payload
// ---------------------------------------------------------------------------

export type TipoConversa = 'INDIVIDUAL' | 'BROADCAST';

export interface ChatUsuario {
  id: string;
  nome: string;
  papel: string | null;
}

export interface UltimaMensagem {
  conteudo: string;
  criadaEm: string;
  remetenteNome: string;
}

export interface ConversaItem {
  id: string;
  tipo: TipoConversa;
  criadaEm: string;
  participantes: { id: string; nome: string }[];
  ultimaMensagem: UltimaMensagem | null;
  naoLidas: number;
}

export interface LeituraItem {
  usuarioId: string;
  nomeUsuario: string;
  lidaEm: string;
}

export interface MensagemItem {
  id: string;
  conteudo: string;
  isBroadcast: boolean;
  solicitaConfirmacaoLeitura: boolean;
  criadaEm: string;
  remetente: { id: string; nome: string };
  leituras: LeituraItem[];
}

export interface MensagensPagina {
  mensagens: MensagemItem[];
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const chatKeys = {
  usuarios: () => ['chat', 'usuarios'] as const,
  conversas: () => ['chat', 'conversas'] as const,
  mensagens: (conversaId: string | null) =>
    ['chat', 'mensagens', conversaId ?? '__none__'] as const,
};

// ---------------------------------------------------------------------------
// Constantes — intervalos de polling
// ---------------------------------------------------------------------------

const POLL_CONVERSAS_MS = 30 * 1000; // 30s — lista de conversas
const POLL_MENSAGENS_MS = 10 * 1000; // 10s — mensagens da conversa aberta
const LIMITE_MENSAGENS_PAG = 50;

// ---------------------------------------------------------------------------
// useChatUsuarios
// ---------------------------------------------------------------------------

export function useChatUsuarios() {
  return useQuery({
    queryKey: chatKeys.usuarios(),
    queryFn: async () => {
      const { data } = await api.get<ChatUsuario[]>('/chat/usuarios');
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// useConversas — motor de polling + toast de nova mensagem (Sprint 22 §5)
// ---------------------------------------------------------------------------

export function useConversas() {
  const location = useLocation();
  const totalAnteriorRef = useRef<number | undefined>(undefined);

  const query = useQuery({
    queryKey: chatKeys.conversas(),
    queryFn: async () => {
      const { data } = await api.get<ConversaItem[]>('/chat/conversas');
      return data;
    },
    refetchInterval: POLL_CONVERSAS_MS,
    refetchIntervalInBackground: false,
  });

  // Toast quando total não-lidas (excluindo broadcasts) aumenta E não estiver
  // em `/chat`. Pattern espelha `BadgePendencias.tsx` (Sprint 18).
  useEffect(() => {
    if (query.data === undefined) return;
    const total = somarNaoLidasNaoBroadcast(query.data);
    const anterior = totalAnteriorRef.current;
    if (anterior !== undefined && total > anterior && !location.pathname.startsWith('/chat')) {
      toast.info('Você tem uma nova mensagem.');
    }
    totalAnteriorRef.current = total;
  }, [query.data, location.pathname]);

  return query;
}

function somarNaoLidasNaoBroadcast(itens: ConversaItem[] | undefined): number {
  if (!itens) return 0;
  return itens.reduce((acc, c) => (c.tipo === 'BROADCAST' ? acc : acc + c.naoLidas), 0);
}

// ---------------------------------------------------------------------------
// useTotalNaoLidasChat — deriva de useConversas (mesma queryKey)
// ---------------------------------------------------------------------------

/**
 * Total de não-lidas excluindo broadcasts. Reusa o cache de [[useConversas]]
 * (mesma queryKey) — sem requisição nova. Como o polling vive em
 * `useConversas`, o badge da sidebar fica fresco a cada 30s (decisão D1 do
 * relatório S22; briefing original pedia 5min, mas isso conflitaria com a
 * regra "sem duplicação de requisição").
 */
export function useTotalNaoLidasChat(): number {
  const { data } = useConversas();
  return useMemo(() => somarNaoLidasNaoBroadcast(data), [data]);
}

// ---------------------------------------------------------------------------
// useMensagens — paginação por cursor + polling 10s
// ---------------------------------------------------------------------------

export interface UseMensagensResult {
  mensagens: MensagemItem[];
  hasMore: boolean;
  carregarMaisAnteriores: () => Promise<void>;
  isLoadingMais: boolean;
  isLoading: boolean;
}

export function useMensagens(conversaId: string | null): UseMensagensResult {
  // Página atual carregada — mantida em estado local para suportar prepend.
  // O cache do react-query guarda só o "topo" (mais recentes). Histórico
  // adicional é anexado aqui.
  const [historicoExtra, setHistoricoExtra] = useState<MensagemItem[]>([]);
  const [hasMoreOverride, setHasMoreOverride] = useState<boolean | null>(null);
  const [isLoadingMais, setIsLoadingMais] = useState(false);

  // Limpa o histórico quando troca de conversa.
  useEffect(() => {
    setHistoricoExtra([]);
    setHasMoreOverride(null);
  }, [conversaId]);

  const query = useQuery({
    queryKey: chatKeys.mensagens(conversaId),
    enabled: !!conversaId,
    queryFn: async () => {
      const { data } = await api.get<MensagensPagina>(
        `/chat/conversas/${conversaId}/mensagens?limite=${LIMITE_MENSAGENS_PAG}`,
      );
      return data;
    },
    refetchInterval: POLL_MENSAGENS_MS,
    refetchIntervalInBackground: false,
  });

  const hasMoreAtual = hasMoreOverride ?? query.data?.hasMore ?? false;
  // Lista final: histórico (mais antigo) primeiro, depois o "topo" do servidor.
  // `mensagensAtual` é derivado de `query.data` dentro do useMemo para evitar
  // que a referência do `?? []` mude a cada render.
  const mensagens = useMemo(() => {
    const mensagensAtual = query.data?.mensagens ?? [];
    return [...historicoExtra, ...mensagensAtual];
  }, [historicoExtra, query.data?.mensagens]);

  const carregarMaisAnteriores = useCallback(async () => {
    if (!conversaId) return;
    const primeiraDaLista = mensagens[0];
    if (!primeiraDaLista) return;
    setIsLoadingMais(true);
    try {
      const { data } = await api.get<MensagensPagina>(
        `/chat/conversas/${conversaId}/mensagens?cursor=${primeiraDaLista.id}&limite=${LIMITE_MENSAGENS_PAG}`,
      );
      setHistoricoExtra((atual) => [...data.mensagens, ...atual]);
      setHasMoreOverride(data.hasMore);
    } finally {
      setIsLoadingMais(false);
    }
  }, [conversaId, mensagens]);

  return {
    mensagens,
    hasMore: hasMoreAtual,
    carregarMaisAnteriores,
    isLoadingMais,
    isLoading: query.isLoading,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useIniciarConversa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (participanteId: string) => {
      const { data } = await api.post<ConversaItem>('/chat/conversas', { participanteId });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatKeys.conversas() });
    },
  });
}

export interface EnviarMensagemInput {
  conteudo: string;
  solicitaConfirmacaoLeitura?: boolean;
}

export function useEnviarMensagem(conversaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EnviarMensagemInput) => {
      const { data } = await api.post<MensagemItem>(
        `/chat/conversas/${conversaId}/mensagens`,
        input,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatKeys.mensagens(conversaId) });
      void qc.invalidateQueries({ queryKey: chatKeys.conversas() });
    },
  });
}

export function useEnviarBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EnviarMensagemInput) => {
      const { data } = await api.post<MensagemItem>('/chat/broadcast', input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatKeys.conversas() });
    },
  });
}

export function useConfirmarLeitura(conversaId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mensagemId: string) => {
      try {
        const { data } = await api.post<{ lidaEm: string; nomeUsuario: string }>(
          `/chat/mensagens/${mensagemId}/leitura`,
        );
        return data;
      } catch (err) {
        // 422 = mensagem não solicita confirmação. Silencioso — não toastar,
        // não propagar (a UI roda confirmação em batch via IntersectionObserver).
        if (axios.isAxiosError(err) && err.response?.status === 422) {
          return null;
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      if (data === null) return; // 422 — nada a invalidar.
      if (conversaId) {
        void qc.invalidateQueries({ queryKey: chatKeys.mensagens(conversaId) });
      }
      void qc.invalidateQueries({ queryKey: chatKeys.conversas() });
    },
  });
}
